// Translations for the idp login + consent surfaces. Bundled inline
// (not lazy-loaded over HTTP like marketplace) — there are only a handful
// of strings and they sit on a latency-sensitive login page. Norwegian and
// Swedish phrasing reuses marketplace's canonical copy where it exists
// (loginHeading, loginBankIDMessage, continue, goBack, ...).
export type AvailableLanguage = 'en' | 'nb' | 'sv'

export const supportedLanguages: Record<AvailableLanguage, string> = {
    en: 'English',
    nb: 'Norsk',
    sv: 'Svenska',
}

export const resources = {
    nb: {
        translations: {
            // Login
            loginHeading: 'Sikker innlogging',
            loginBankIDMessage:
                'Vi bruker BankID for innlogging, slik sikrer vi at informasjon om deg og din bedrift er trygg.',
            showOtherMethods: 'Vis andre påloggingsmetoder',
            otherMethods: 'Andre metoder',
            signedInHeading: 'Du er innlogget',
            signedInAs: 'Innlogget som <0>{{name}}</0>.',
            continue: 'Gå videre',
            switchUser: 'Logg inn som noen andre',
            // Consent. `consent.<resource>` overrides `consent.default` per
            // audience; an empty description/note/disclaimer/acknowledgement is
            // rendered as nothing, and an empty acknowledgement means no checkbox.
            consent: {
                default: {
                    heading: 'Gi tilgang til {{client}}',
                    description: '{{client}} ønsker tilgang til <0>{{resource}}</0> på dine vegne.',
                    note: '',
                    disclaimer: '',
                    acknowledgement: '',
                },
                'fixrate-customer-mcp': {
                    heading: 'Godkjenne tilgang',
                    description: 'Vil du gi {{client}} tilgang til din bedrift på Fixrate?',
                    note: 'Ved å trykke «Godkjenn» godkjenner du at informasjon om din organisasjon og dine plasseringer gjøres tilgjengelig for {{client}} i {{duration}}.',
                    disclaimer: '{{client}} er en AI-tjeneste du selv har valgt, og som Fixrate ikke drifter eller kontrollerer. Når du godkjenner, kan tjenesten hente data om organisasjonen din og plasseringene deres fra Fixrate.',
                    acknowledgement: 'Jeg forstår at {{client}} får tilgang til organisasjonens data på Fixrate, og at Fixrate ikke er ansvarlig for hvordan {{client}} bruker dataene eller for resultatene tjenesten gir.',
                },
                'fixrate-internal-mcp': {
                    heading: 'Godkjenne tilgang',
                    description: 'Vil du gi {{client}} tilgang til administrative data på Fixrate?',
                    note: 'Ved å trykke «Godkjenn» godkjenner du at administrative data gjøres tilgjengelig for {{client}} i {{duration}}.',
                },
            },
            accessDuration: 'Varighet på tilgang',
            organisationsLabel: 'Organisasjoner klienten kan opptre på vegne av',
            noAssociationsTitle: 'Denne kontoen har ikke tilgang',
            noAssociationsBody: 'Kontoen du er innlogget med har ingen organisasjoner som kan gi denne tilgangen. Logg inn med en annen konto for å fortsette.',
            switchAccount: 'Logg inn med en annen konto',
            selectAll: 'Velg alle',
            unselectableHint: 'Noen organisasjoner er ikke aktivert for {{client}}. Ta kontakt med Fixrate kundeservice for å aktivere tilgangen.',
            noSelectableBody: 'Ingen av organisasjonene dine er aktivert for {{client}}. Ta kontakt med Fixrate kundeservice for å få tilgangen aktivert.',
            loginToInclude: 'Logg inn med {{idp}} for å kunne ta med denne organisasjonen',
            deny: 'Avvis',
            allow: 'Godkjenn',
            // TTL options
            ttl30m: '30 minutter',
            ttl8h: '8 timer',
            ttl30d: '30 dager',
            // Errors
            errorTitle: 'Vi kunne ikke fullføre forespørselen',
            // Loading
            loading: 'Laster …',
        },
    },
    sv: {
        translations: {
            // Login
            loginHeading: 'Säker inloggning',
            loginBankIDMessage:
                'Vi använder BankID för inloggning, detta säkerställer att information om dig och din organisation är säker.',
            showOtherMethods: 'Visa andra inloggningsmetoder',
            otherMethods: 'Andra metoder',
            signedInHeading: 'Du är inloggad',
            signedInAs: 'Inloggad som <0>{{name}}</0>.',
            continue: 'Fortsätt',
            switchUser: 'Logga in som någon annan',
            // Consent. `consent.<resource>` overrides `consent.default` per
            // audience; an empty description/note/disclaimer/acknowledgement is
            // rendered as nothing, and an empty acknowledgement means no checkbox.
            consent: {
                default: {
                    heading: 'Ge åtkomst till {{client}}',
                    description: '{{client}} vill ha åtkomst till <0>{{resource}}</0> å dina vägnar.',
                    note: '',
                    disclaimer: '',
                    acknowledgement: '',
                },
                'fixrate-customer-mcp': {
                    heading: 'Godkänn åtkomst',
                    description: 'Vill du ge {{client}} åtkomst till ditt företag på Fixrate?',
                    note: 'Genom att trycka på ”Godkänn” godkänner du att information om din organisation och dina placeringar görs tillgänglig för {{client}} i {{duration}}.',
                    disclaimer: '{{client}} är en AI-tjänst som du själv har valt och som Fixrate varken driver eller kontrollerar. När du godkänner kan tjänsten hämta data om din organisation och era placeringar från Fixrate.',
                    acknowledgement: 'Jag förstår att {{client}} får åtkomst till organisationens data på Fixrate, och att Fixrate inte ansvarar för hur {{client}} använder dessa data eller för de resultat tjänsten ger.',
                },
                'fixrate-internal-mcp': {
                    heading: 'Godkänn åtkomst',
                    description: 'Vill du ge {{client}} åtkomst till administrativa data på Fixrate?',
                    note: 'Genom att trycka på ”Godkänn” godkänner du att administrativa data görs tillgängliga för {{client}} i {{duration}}.',
                },
            },
            accessDuration: 'Åtkomstens varaktighet',
            organisationsLabel: 'Organisationer som klienten får agera för',
            noAssociationsTitle: 'Det här kontot har inte åtkomst',
            noAssociationsBody: 'Kontot du är inloggad med har inga organisationer som kan ge den här åtkomsten. Logga in med ett annat konto för att fortsätta.',
            switchAccount: 'Logga in med ett annat konto',
            selectAll: 'Välj alla',
            unselectableHint: 'Vissa organisationer är inte aktiverade för {{client}}. Kontakta Fixrate kundtjänst för att aktivera åtkomsten.',
            noSelectableBody: 'Ingen av dina organisationer är aktiverad för {{client}}. Kontakta Fixrate kundtjänst för att få åtkomsten aktiverad.',
            loginToInclude: 'Logga in med {{idp}} för att kunna ta med denna organisation',
            deny: 'Avvisa',
            allow: 'Godkänn',
            // TTL options
            ttl30m: '30 minuter',
            ttl8h: '8 timmar',
            ttl30d: '30 dagar',
            // Errors
            errorTitle: 'Vi kunde inte slutföra din begäran',
            // Loading
            loading: 'Laddar …',
        },
    },
    en: {
        translations: {
            // Login
            loginHeading: 'Secure sign-in',
            loginBankIDMessage:
                'We use BankID to keep information about you and your company safe.',
            showOtherMethods: 'Show other sign-in methods',
            otherMethods: 'Other methods',
            signedInHeading: 'You are signed in',
            signedInAs: 'Signed in as <0>{{name}}</0>.',
            continue: 'Continue',
            switchUser: 'Sign in as someone else',
            // Consent. `consent.<resource>` overrides `consent.default` per
            // audience; an empty description/note/disclaimer/acknowledgement is
            // rendered as nothing, and an empty acknowledgement means no checkbox.
            consent: {
                default: {
                    heading: 'Authorise {{client}}',
                    description: '{{client}} wants access to <0>{{resource}}</0> on your behalf.',
                    note: '',
                    disclaimer: '',
                    acknowledgement: '',
                },
                'fixrate-customer-mcp': {
                    heading: 'Approve access',
                    description: 'Do you want to give {{client}} access to your company on Fixrate?',
                    note: 'By clicking “Allow” you agree that information about your organisation and your deposits is made available to {{client}} for {{duration}}.',
                    disclaimer: '{{client}} is an AI service you have chosen yourself, and which Fixrate neither operates nor controls. When you approve, the service can retrieve data about your organisation and its placements from Fixrate.',
                    acknowledgement: 'I understand that {{client}} will have access to my organisation’s data on Fixrate, and that Fixrate is not responsible for how {{client}} uses that data or for the results the service produces.',
                },
                'fixrate-internal-mcp': {
                    heading: 'Approve access',
                    description: 'Do you want to give {{client}} access to administrative data on Fixrate?',
                    note: 'By clicking “Allow” you agree that administrative data is made available to {{client}} for {{duration}}.',
                },
            },
            accessDuration: 'Access duration',
            organisationsLabel: 'Organisations the client may act for',
            noAssociationsTitle: 'This account does not have access',
            noAssociationsBody: 'The account you are signed in with has no organisations that can grant this access. Sign in with another account to continue.',
            switchAccount: 'Sign in with another account',
            selectAll: 'Select all',
            unselectableHint: 'Some organisations are not enabled for {{client}}. Contact Fixrate customer service to enable access.',
            noSelectableBody: 'None of your organisations are enabled for {{client}}. Contact Fixrate customer service to have access enabled.',
            loginToInclude: 'Sign in with {{idp}} to be able to include this organisation',
            deny: 'Deny',
            allow: 'Allow',
            // TTL options
            ttl30m: '30 minutes',
            ttl8h: '8 hours',
            ttl30d: '30 days',
            // Errors
            errorTitle: 'We couldn’t complete your request',
            // Loading
            loading: 'Loading …',
        },
    },
} as const
